import axios from "axios";
import { clearNativeAuthTokens, getNativeAuthTokens, isNativeApp, nativeAuthGeneration, storeNativeAuthTokens } from "./nativeAuthStorage";
export { clearNativeAuthTokens, storeNativeAuthTokens } from "./nativeAuthStorage";

function normalizeBackendUrl(value) {
  const trimmed = (value || "").trim().replace(/\/+$/, "");
  return trimmed.replace(/\/api\/health$/, "").replace(/\/api$/, "");
}

const backendUrl = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);

const api = axios.create({
  baseURL: `${backendUrl}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    ...(isNativeApp() ? { "X-Native-App": "true" } : {}),
  },
});

api.interceptors.request.use(async (config) => {
  if (!isNativeApp()) return config;
  const tokens = await getNativeAuthTokens();
  const token = tokens.access_token;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (/\/auth\/(refresh|logout)$/.test(config.url || "") && tokens.refresh_token) {
    config.headers = config.headers || {};
    config.headers["X-Refresh-Token"] = tokens.refresh_token;
  }
  return config;
});

// Refresh expired access credentials once, using HttpOnly cookies on the web
// and protected tokens on native platforms. Concurrent 401s share one refresh.
// Temporary connection failures preserve the saved login for the next attempt.
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (!original || status !== 401 || original._retried) {
      return Promise.reject(error);
    }
    const url = (original.url || "").toString();
    if (/\/auth\/(refresh|login|logout)$/.test(url)) {
      return Promise.reject(error);
    }
    original._retried = true;

    // Coalesce concurrent refresh attempts so a burst of 401s only triggers
    // one /auth/refresh call.
    if (!refreshPromise) {
      const generation = nativeAuthGeneration();
      refreshPromise = api
        .post("/auth/refresh", null)
        .then(async (res) => {
          await storeNativeAuthTokens(res.data, generation);
          return res;
        })
        .finally(() => { refreshPromise = null; });
    }
    try {
      await refreshPromise;
    } catch (refreshErr) {
      // Refresh token gone or invalid — send the user to login. Use window
      // location so we tear down the React tree cleanly. Guard against the
      // login page itself triggering this.
      if ([401, 403].includes(refreshErr?.response?.status)) {
        await clearNativeAuthTokens();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
      }
      return Promise.reject(refreshErr);
    }
    return api(original);
  },
);

export default api;

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
